<template>
  <div v-if="!isLoading && !isProductsLoading">
    <rule-create-modal @onRuleSave="storeRule" :mappingRules="mappingRules" :sourceVariables="sourceVariables" :templateFields="availableFields"></rule-create-modal>
    <rule-edit-modal @onRuleSave="updateRule" :currentRule="currentRule" :mappingRules="mappingRules" :sourceVariables="sourceVariables" :templateFields="availableFields"></rule-edit-modal>
    <div class="mb-5">
      <p>{{$t('feeds.rules_description')}}</p>
      <p>{{ $t('feeds.rules_override_warning') }}</p>
    </div>
    <transition-group name="fade" mode="out-in" tag="div">
      <div v-for="(rule, index) in rules" v-bind:key="index+1" class="dashboard-content rule-container py-4 mb-5 d-flex justify-content-between">
        <div class="rule-title pt-3">
          <h6 class="mb-0">{{$t('feeds.rule')}} #{{ index + 1 }}</h6>
        </div>
        <div class="d-flex flex-column rule-content">
          <div class="d-flex align-items-center">
            <p class="mb-0 mr-4">{{$t('feeds.for_variable')}}</p>
            <div class="custom-label bg-purple px-5 mr-2">{{ rule.sourceVariable }}</div>
            <p class="mb-0 mx-4">{{$t('feeds.used_in_field_2')}}</p>
            <div class="custom-label bg-orange-light px-5 mr-4">{{ rule.columnName }}</div>
          </div>
          <div class="d-flex mt-4 align-items-center">
            <strong class="mr-4">{{ rule.operationTypeLabel }}</strong>
            <div v-if="rule.operationType === 'replace'" class="d-flex align-items-center ml-3">
              <div class="custom-label bg-gray px-5">{{ rule.toReplace }}</div>
              <p class="mb-0 mx-4">{{ $t('global.to') }}</p>
              <div class="custom-label bg-gray px-5">{{ rule.replaceWith }}</div>
            </div>
            <div v-else-if="rule.operationType === 'value' || rule.operationType === 'format' || rule.operationType === 'choose'" class="d-flex ml-3">
              <div class="custom-label bg-gray px-5">{{ rule.replaceWith }}</div>
            </div>
            <div v-else-if="rule.operationType === 'regex match'" class="d-flex ml-3">
              <div class="custom-label bg-gray px-5">{{ rule.toReplace }}</div>
            </div>
            <div v-else class="d-flex ml-3 align-items-center">
              <div class="custom-label bg-gray px-5">{{ rule.toReplace }}</div>
              <p class="mb-0 mx-3">-></p>
              <div class="custom-label bg-gray px-5">{{ rule.replaceWith }}</div>
            </div>
            <p class="mb-0 ml-4">{{ $t('global.taking_into_account') }} <strong>{{ rule.conditions.length }}</strong> {{$t('global.conditions2')}}</p>
          </div>
        </div>
        <div class="d-flex justify-content-end rule-actions">
          <button v-if="index > 0" @click="moveRuleUp(index, rule)" class="btn btn-gray btn-toggle mr-4">
            <img class="rotated" src="/app/images/icons/toggle.svg">
          </button>
          <button v-if="index < rules.length - 1" @click="moveRuleDown(index, rule)" class="btn btn-gray btn-toggle mr-4">
            <img src="/app/images/icons/toggle.svg">
          </button>
          <button class="btn btn-small btn-orange mr-4 px-4" @click="editRule(index, rule)">
            <edit-icon></edit-icon>
            <span>{{ $t('global.change') }}</span>
          </button>
          <button class="btn btn-small btn-gray-dark" @click="deleteRule(index)">
            <trash-2-icon></trash-2-icon>
            <span>{{ $t('global.delete') }}</span>
          </button>
        </div>
      </div>
    </transition-group>
    <div class="d-flex justify-content-between">
      <button class="btn btn-medium btn-orange" @click="$bvModal.show('ruleCreateModal')">
        <plus-circle-icon></plus-circle-icon>
        <span>{{ $t('feeds.add_rule') }}</span>
      </button>
      <div class="d-flex" v-if="$route.params.feedId !== undefined">
        <router-link :to="{ name: 'feed.edit.field-mapping', params: { id: $route.params.projectId, feedId: feed.id, feed: feed }}" class="mr-4">
          <button class="btn btn-large btn-gray-dark">
            <arrow-left-circle-icon></arrow-left-circle-icon>
            <span>{{ $t('global.back') }}</span>
          </button>
        </router-link>
        <router-link :to="{ name: 'feed.edit.filters', params: { id: $route.params.projectId, feedId: feed.id, feed: feed }}">
          <button class="btn btn-large btn-orange-dark">
            <arrow-right-circle-icon></arrow-right-circle-icon>
            <span>{{$t('global.next')}}</span>
          </button>
        </router-link>
      </div>
      <div class="d-flex" v-else>
        <router-link :to="{ name: 'feed.create.field-mapping', params: { id: $route.params.projectId, feed: feed }}" class="mr-4">
          <button class="btn btn-large btn-gray-dark">
            <arrow-left-circle-icon></arrow-left-circle-icon>
            <span>{{ $t('global.back') }}</span>
          </button>
        </router-link>
        <router-link :to="{ name: 'feed.create.filters', params: { id: $route.params.projectId, feed: feed }}">
          <button class="btn btn-large btn-orange-dark">
            <arrow-right-circle-icon></arrow-right-circle-icon>
            <span>{{$t('global.next')}}</span>
          </button>
        </router-link>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="spinner-border text-orange" role="status"></div>
  </div>
</template>

<script>
import { ArrowLeftCircleIcon, ArrowRightCircleIcon, EditIcon, PlusCircleIcon, Trash2Icon } from 'vue-feather-icons'
import RuleCreateModal from './Rules/RuleCreateModal'
import RuleEditModal from './Rules/RuleEditModal'
import feeds from '@/mixins/feeds'

export default {
  name: 'Rules',

  data () {
    return {
      currentIndex: null,
      currentRule: {},
      mappingRules: this.feed.config.mappingRules,
      rules: this.feed.config.formatRules
    }
  },

  props: {
    feed: { required: true, type: Object }
  },

  mixins: [feeds],

  created () {
    this.initSourceVariables(this.feed.feedType)
  },

  methods: {
    storeRule (rule) {
      this.rules.push(rule)
    },

    editRule (index) {
      this.currentIndex = index
      this.currentRule = this.rules[index]
      setTimeout(() => { this.$bvModal.show('ruleEditModal') }, 50)
    },

    updateRule (rule) {
      this.rules[this.currentIndex].columnName = rule.columnName
      this.rules[this.currentIndex].conditions = rule.conditions
      this.rules[this.currentIndex].mappingRule = rule.mappingRule
      this.rules[this.currentIndex].operationType = rule.operationType
      this.rules[this.currentIndex].operationTypeLabel = rule.operationTypeLabel
      this.rules[this.currentIndex].options = rule.options
      this.rules[this.currentIndex].replaceWith = rule.replaceWith
      this.rules[this.currentIndex].sourceVariable = rule.sourceVariable
      this.rules[this.currentIndex].toReplace = rule.toReplace
    },

    deleteRule (index) {
      this.rules.splice(index, 1)
    },

    moveRuleUp (index, rule) {
      this.rules.splice(index, 1)
      this.rules.splice(index - 1, 0, rule)
    },

    moveRuleDown (index, rule) {
      this.rules.splice(index, 1)
      this.rules.splice(index + 1, 0, rule)
    }
  },

  components: { ArrowLeftCircleIcon, ArrowRightCircleIcon, EditIcon, PlusCircleIcon, RuleCreateModal, RuleEditModal, Trash2Icon }
}
</script>
